import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useApp } from "../context/AppContext";
import { uid } from "../utils/helpers";
import ProjectInfo from "../components/ProjectInfo";

export default function ListView() {
  const { proj, activeId, getStore, setStore, dataRef, projects, setProjects, apiFetch,
    saveClusters, saveContent, saveStructure, saveSemClusters, saveLinks,
    flash, setView, view, dark, setDark, t, card, inp, btnP, btnG, btnSm, stitle, muted, th, td,
    userRole, currentUser, loadProjectData, openP, infoId, setInfoId, setActiveId,
    projectsTotal, projectsSearch, setProjectsSearch, projectsLoading, loadProjects, loadMoreProjects } = useApp();

  // Local search state with debounce
  const [searchInput, setSearchInput] = useState(projectsSearch || "");
  const debounceRef = useRef(null);

  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setProjectsSearch(val);
      loadProjects(val, 0, false);
    }, 300);
  }, [setProjectsSearch, loadProjects]);

  // Calculate which projects need invoice
  const invoiceNeeded = useMemo(() => {
    const result = {};
    const now = new Date();
    (projects || []).forEach(p => {
      if (!p.startDate) return;
      const start = new Date(p.startDate);
      if (isNaN(start.getTime())) return;
      let periodStart = new Date(start);
      while (periodStart <= now) {
        const next = new Date(periodStart);
        next.setMonth(next.getMonth() + 1);
        if (next > now) break;
        periodStart = next;
      }
      const periodEnd = new Date(periodStart);
      periodEnd.setMonth(periodEnd.getMonth() + 1);
      const daysLeft = Math.ceil((periodEnd - now) / (1000 * 60 * 60 * 24));
      if (daysLeft <= 5) result[p.id] = daysLeft;
    });
    return result;
  }, [projects]);

  // Group projects by version group (same site, multiple language versions)
  const [activeVersion, setActiveVersion] = useState({});
  const projectGroups = useMemo(() => {
    const map = new Map();
    const order = [];
    (projects || []).forEach(p => {
      const key = p.versionGroupId || p.id;
      if (!map.has(key)) { map.set(key, []); order.push(key); }
      map.get(key).push(p);
    });
    const rank = l => l === "RU" ? 0 : l === "UKR" ? 1 : l ? 2 : 3;
    return order.map(key => {
      const versions = [...map.get(key)].sort((a, b) => rank(a.lang) - rank(b.lang));
      return { key, versions };
    });
  }, [projects]);

  const [nn, setNN] = useState("");
  const [nd, setND] = useState("");
  const [tip, setTip] = useState(null); // { x, y, text }
  // Підтвердження видалення проєкту (незворотня операція — треба ввести назву).
  const [delTarget, setDelTarget] = useState(null); // проєкт, який видаляємо
  const [delText, setDelText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const confirmDeleteProject = async () => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/api/projects/${delTarget.id}`, { method: "DELETE" });
      setProjects(prev => prev.filter(x => x.id !== delTarget.id));
      delete dataRef.current[delTarget.id];
      flash(`Проєкт «${delTarget.name}» видалено`);
    } catch { flash("Помилка видалення"); }
    setDeleting(false);
    setDelTarget(null);
    setDelText("");
  };
  const showTip = useCallback((e, text) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTip({ x: r.left + r.width / 2, y: r.top, text });
  }, []);
  const hideTip = useCallback(() => setTip(null), []);
  const create = async () => {
    if (!nn.trim()) return;
    const p = { id: uid(), name: nn.trim(), domain: nd.trim(), competitors: [], cc: 0, rc: 0,
      startDate: "", payment: "", budget: { base: "", dev: "", links: "", crowd: "", copy: "" },
      contractUrl: "", contractor: "", entityType: "", lang: "", version: "", myRole: "owner" };
    try {
      await apiFetch("/api/projects", { method: "POST", body: JSON.stringify(p) });
    } catch (e) { console.error("Create project:", e); }
    setProjects(prev => [p, ...prev]);
    setStore(p.id, { rows: [], clusters: [] });
    setNN(""); setND(""); openP(p.id);
  };
  const updateProj = (id, updates) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
    apiFetch(`/api/projects/${id}`, { method: "PUT", body: JSON.stringify(updates) }).catch(() => {});
  };

  const hasMore = projects.length < projectsTotal;

  return (
    <div>
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 20 }}>Проекти</h2>

      {/* Search */}
      <div style={{ ...card, display: "flex", gap: 8, alignItems: "center" }}>
        <input
          placeholder="Пошук по назві або домену..."
          value={searchInput}
          onChange={e => handleSearch(e.target.value)}
          style={{ ...inp, flex: 1 }}
        />
        <span style={{ fontSize: 12, color: t.textMuted, whiteSpace: "nowrap" }}>
          {projects.length} з {projectsTotal}
        </span>
      </div>

      {/* New project */}
      <div style={card}>
        <p style={stitle}>Новий проект</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input placeholder="Назва" value={nn} onChange={e => setNN(e.target.value)} style={{ ...inp, flex: 1, minWidth: 140 }} onKeyDown={e => e.key === "Enter" && create()} />
          <input placeholder="Домен" value={nd} onChange={e => setND(e.target.value)} style={{ ...inp, flex: 1, minWidth: 140 }} onKeyDown={e => e.key === "Enter" && create()} />
          <button onClick={create} style={btnP}>+ Створити</button>
        </div>
      </div>

      {projects.length === 0 && !projectsLoading && <p style={{ textAlign: "center", color: t.textMuted, padding: 30 }}>Немає проектів</p>}

      {projectGroups.map((group) => {
        const activeVId = activeVersion[group.key] || group.versions[0].id;
        const p = group.versions.find(v => v.id === activeVId) || group.versions[0];
        const langColor = (lng) => lng === "UKR" ? "#6366f1" : lng === "RU" ? "#f59e0b" : "#22c55e";
        return (
        <div key={group.key} style={{ ...card, padding: 0, overflow: "hidden" }}>
          {/* Version tabs (only when site has >1 language version) */}
          {group.versions.length > 1 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 18px", borderBottom: `1px solid ${t.border}`, background: dark ? "rgba(124,58,237,0.07)" : "rgba(124,58,237,0.04)", flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#7c3aed", marginRight: 2 }}>🌐 {group.versions.length} версії</span>
              {group.versions.map(v => {
                const isAct = v.id === p.id;
                // підпис версії: мова (UKR/RU) якщо є, інакше назва версії (місто), інакше назва без префіксу бренду
                const vlabel = v.lang || v.version || (v.name ? v.name.replace(/^Sushi Master\s*/i, "").trim() : "") || "—";
                const col = v.lang ? langColor(v.lang) : "#6366f1";
                return (
                  <button key={v.id} title={v.domain || v.name} onClick={e => { e.stopPropagation(); setActiveVersion(prev => ({ ...prev, [group.key]: v.id })); }}
                    style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6, cursor: "pointer", border: `1.5px solid ${col}`, background: isAct ? col : "transparent", color: isAct ? "#fff" : col, transition: "all .15s" }}>
                    {vlabel}
                  </button>
                );
              })}
            </div>
          )}
          {/* Project card header */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px", cursor: "pointer" }} onClick={() => openP(p.id)}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: "linear-gradient(135deg,#6366f1,#a855f7)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>{p.name[0]?.toUpperCase()}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name}</div>
              <div style={muted}>{p.domain || "—"} · {p.cc || 0} стр.{p.startDate ? ` · з ${p.startDate}` : ""}</div>
            </div>
            {p.stats && (p.stats.top3Pct != null || p.stats.top10Pct != null || p.stats.aiVisPct != null || p.stats.gscClicks24h != null) && (() => {
              const tr = (v) => v > 0
                ? { a: "▲", c: "#16a34a" }
                : v < 0 ? { a: "▼", c: "#ef4444" } : { a: "", c: t.textMuted };
              const Chip = ({ label, pct, trend, accent, value, trendPct, tooltip }) => {
                const display = value != null ? value : (pct != null ? `${pct}%` : null);
                if (display == null) return null;
                const m = trend !== undefined ? tr(trend) : null;
                const tp = trendPct !== undefined && trendPct !== null ? tr(trendPct) : null;
                return (
                  <div title={tooltip} style={{
                    display: "flex", flexDirection: "column", alignItems: "center",
                    minWidth: 52, padding: "3px 8px", borderRadius: 7,
                    background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                  }}>
                    <span style={{ fontSize: 8.5, letterSpacing: ".06em", color: t.textMuted, textTransform: "uppercase" }}>{label}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: accent || t.text, lineHeight: 1.25, display: "flex", alignItems: "center", gap: 3 }}>
                      {display}
                      {m && m.a && <span style={{ fontSize: 9, color: m.c }}>{m.a}</span>}
                      {tp && tp.a && <span style={{ fontSize: 9, color: tp.c, fontWeight: 600 }}>{tp.a}{Math.abs(trendPct)}%</span>}
                    </span>
                  </div>
                );
              };
              const sign = p.stats.gscClicksTrend;
              const trendSign = sign == null ? undefined : (sign > 0 ? 1 : sign < 0 ? -1 : 0);
              return (
                <div style={{ display: "flex", gap: 6, marginRight: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                  <Chip label="ТОП-3" pct={p.stats.top3Pct} trend={p.stats.top3Trend} />
                  <Chip label="ТОП-10" pct={p.stats.top10Pct} trend={p.stats.top10Trend} />
                  {p.stats.gscClicks24h != null && (
                    <Chip
                      label="GSC 24г"
                      value={p.stats.gscClicks24h}
                      trendPct={p.stats.gscClicksTrend}
                      accent="#16a34a"
                      tooltip={`Кліки за останню добу: ${p.stats.gscClicks24h}\nПопередня доба: ${p.stats.gscClicksTrend != null ? Math.round(p.stats.gscClicks24h / (1 + p.stats.gscClicksTrend / 100)) : '—'}\nПокази за добу: ${p.stats.gscImps24h ?? '—'}`}
                    />
                  )}
                  <Chip label="AI" pct={p.stats.aiVisPct} accent="#a855f7" />
                </div>
              );
            })()}
            {p.members && p.members.length > 0 && (() => {
              const workers = p.members.filter(m => m.role === "owner" || m.role === "editor");
              if (!workers.length) return null;
              const colorFor = (s) => {
                let h = 0;
                for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
                const hue = Math.abs(h) % 360;
                return `hsl(${hue}, 55%, 50%)`;
              };
              const initials = (m) => {
                const src = (m.fullName || m.username || "?").trim();
                const parts = src.split(/[\s@._-]+/).filter(Boolean);
                return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || src[0]?.toUpperCase() || "?";
              };
              const visible = workers.slice(0, 3);
              const extra = workers.length - visible.length;
              return (
                <div style={{ display: "flex", alignItems: "center", marginRight: 4 }} onMouseLeave={hideTip}>
                  {visible.map((m, i) => (
                    <div key={m.userId}
                      onMouseEnter={e => { e.stopPropagation(); showTip(e, `${m.fullName || m.username} · ${m.role === "owner" ? "Власник" : "Редактор"}`); }}
                      style={{
                        width: 26, height: 26, borderRadius: "50%",
                        background: colorFor(m.username || String(m.userId)),
                        color: "#fff", fontSize: 10, fontWeight: 700,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: `2px solid ${dark ? "#14141e" : "#fff"}`,
                        marginLeft: i === 0 ? 0 : -8, position: "relative", zIndex: visible.length - i,
                        boxShadow: m.role === "owner" ? "0 0 0 1px #6366f1" : "none",
                        cursor: "default"
                      }}
                    >{initials(m)}</div>
                  ))}
                  {extra > 0 && (
                    <div onMouseEnter={e => { e.stopPropagation(); showTip(e, workers.slice(3).map(m => m.fullName || m.username).join(", ")); }} style={{
                      width: 26, height: 26, borderRadius: "50%",
                      background: dark ? "#1e1e2c" : "#e5e7eb", color: t.textMuted,
                      fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
                      border: `2px solid ${dark ? "#14141e" : "#fff"}`, marginLeft: -8, position: "relative", cursor: "default"
                    }}>+{extra}</div>
                  )}
                </div>
              );
            })()}
            {currentUser === "iz@svitlo.agency" && invoiceNeeded[p.id] !== undefined && (
              <span onClick={e => { e.stopPropagation(); setInfoId(p.id); }} style={{
                fontSize: 10, fontWeight: 700, padding: "3px 10px", borderRadius: 8, cursor: "pointer",
                background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)",
                animation: "pulse 1.5s infinite",
              }}>Рахунок ({invoiceNeeded[p.id]}д)</span>
            )}
            {p.myRole && p.myRole !== "owner" && <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 6, fontWeight: 700, background: p.myRole === "editor" ? "rgba(245,158,11,0.12)" : "rgba(100,116,139,0.12)", color: p.myRole === "editor" ? "#f59e0b" : "#64748b" }}>{p.myRole === "editor" ? "Редактор" : "Глядач"}</span>}
            {p.lang && <span style={{ fontSize: 9, padding: "2px 8px", borderRadius: 6, fontWeight: 700, background: p.lang === "UKR" ? "rgba(99,102,241,0.12)" : p.lang === "RU" ? "rgba(245,158,11,0.12)" : "rgba(34,197,94,0.12)", color: p.lang === "UKR" ? "#6366f1" : p.lang === "RU" ? "#f59e0b" : "#22c55e" }}>{p.lang}</span>}
            {p.payment && <span style={{ fontSize: 11, background: dark ? "#1e1e2c" : "#f0fdf4", color: "#16a34a", padding: "2px 8px", borderRadius: 6, fontWeight: 600 }}>{p.payment}</span>}
            {p.version && <button onClick={e => { e.stopPropagation(); setInfoId(infoId === p.id ? null : p.id); }} style={{ fontSize: 11, background: infoId === p.id ? "#7c3aed" : (dark ? "#1e1e2c" : "#ede9fe"), color: infoId === p.id ? "#fff" : "#7c3aed", padding: "2px 8px", borderRadius: 6, fontWeight: 600, cursor: "pointer", border: "none", lineHeight: "inherit", transition: "all .15s" }}>{p.version}</button>}
            <button style={btnSm} onClick={e => { e.stopPropagation(); setInfoId(infoId === p.id ? null : p.id); }} title="Інформація по проекту">✏️</button>
            <span style={{ color: "#ccc" }} onClick={() => openP(p.id)}>›</span>
            {(p.myRole === "owner" || userRole === "admin") && <button style={{ ...btnSm, color: "#e53935" }} title="Видалити проєкт" onClick={e => { e.stopPropagation(); setDelText(""); setDelTarget(p); }}>🗑</button>}
          </div>

          {/* Expandable project info */}
          {infoId === p.id && <ProjectInfo proj={p} updateProj={updateProj} t={t} inp={inp} dark={dark} />}
        </div>
        );
      })}

      {/* Load more */}
      {hasMore && (
        <div style={{ textAlign: "center", padding: 16 }}>
          <button onClick={loadMoreProjects} disabled={projectsLoading} style={btnG}>
            {projectsLoading ? "Завантаження..." : `Завантажити ще (${projectsTotal - projects.length} залишилось)`}
          </button>
        </div>
      )}
      {projectsLoading && projects.length === 0 && (
        <p style={{ textAlign: "center", color: t.textMuted, padding: 20 }}>Завантаження...</p>
      )}

      {tip && createPortal(
        <div style={{
          position: "fixed",
          left: tip.x,
          top: tip.y - 10,
          transform: "translate(-50%, -100%)",
          background: "#0c0c10",
          color: "#ededf0",
          padding: "6px 10px",
          borderRadius: 6,
          fontSize: 11,
          fontWeight: 500,
          whiteSpace: "nowrap",
          border: "1px solid rgba(255,255,255,0.12)",
          boxShadow: "0 6px 24px rgba(0,0,0,0.5)",
          pointerEvents: "none",
          zIndex: 99999
        }}>
          {tip.text}
        </div>,
        document.body
      )}

      {delTarget && createPortal(
        <div onClick={() => !deleting && setDelTarget(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 100000, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 460, maxWidth: "100%", background: dark ? "#17171f" : "#fff", border: `1px solid ${dark ? "#3a2020" : "#fecaca"}`, borderRadius: 14, padding: 24, boxShadow: "0 20px 60px rgba(0,0,0,0.4)" }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#ef4444", marginBottom: 8 }}>⚠️ Видалити проєкт?</div>
            <div style={{ fontSize: 13, color: dark ? "#c9c9d4" : "#374151", lineHeight: 1.6, marginBottom: 4 }}>
              Проєкт <b style={{ color: dark ? "#fff" : "#111" }}>{delTarget.name}</b>{delTarget.domain ? ` (${delTarget.domain})` : ""} і <b>ВСІ його дані</b> — семантика, позиції, тексти, структура, звіти, беклінки — буде видалено <b>назавжди</b>.
            </div>
            <div style={{ fontSize: 12, color: dark ? "#8b8b9a" : "#6b7280", marginBottom: 14 }}>Це незворотньо. Відновити можна лише зі щоденного бекапу.</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: dark ? "#c9c9d4" : "#374151", marginBottom: 6 }}>Щоб підтвердити, введіть назву проєкту: <span style={{ color: "#ef4444" }}>{delTarget.name}</span></div>
            <input autoFocus value={delText} onChange={e => setDelText(e.target.value)} placeholder={delTarget.name}
              onKeyDown={e => { if (e.key === "Enter" && delText.trim() === (delTarget.name || "").trim() && !deleting) confirmDeleteProject(); }}
              style={{ width: "100%", padding: "9px 12px", fontSize: 14, borderRadius: 8, border: `1px solid ${dark ? "#2a2a35" : "#e5e7eb"}`, background: dark ? "#0f0f14" : "#fff", color: dark ? "#e4e4ec" : "#1a1a2e", boxSizing: "border-box", marginBottom: 18, fontFamily: "inherit" }} />
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDelTarget(null)} disabled={deleting} style={{ ...btnG, padding: "9px 16px" }}>Скасувати</button>
              <button onClick={confirmDeleteProject} disabled={deleting || delText.trim() !== (delTarget.name || "").trim()}
                style={{ padding: "9px 18px", borderRadius: 8, border: "none", fontSize: 13, fontWeight: 700, color: "#fff", fontFamily: "inherit", background: (delText.trim() === (delTarget.name || "").trim() && !deleting) ? "#ef4444" : "#9ca3af", cursor: (delText.trim() === (delTarget.name || "").trim() && !deleting) ? "pointer" : "not-allowed" }}>
                {deleting ? "Видаляю…" : "Видалити назавжди"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
